import React, { FC } from "react";
import { H3, Radio, Switch } from "@evergis/ui";
import { useTranslation } from "react-i18next";

import { isReadable } from "../../../../../evergis/core/permissions";
import { declension } from "../../../../../utils/declension";
import { formatNumber } from "../../../../../utils/formatNumber";
import { isInfinityLimit } from "../../../../../hooks/useLimitsExceededCheck";

import { RestrictedWidthContainer } from "../../../DialogComponents/styled";
import { FILTERED_OPTIONS } from "../../constants";
import { Notice } from "../Project/styled";

import {
  Description,
  LabelContainer,
  LayerCopyFilteredOptionsContainer,
  LayerCopyOptionsContainer,
  OptionsRadioContainer,
  OptionsRadioGroup,
} from "./styled";

import { LayerCopyOptionsProps } from "./types";

export const CopyLayerOptions: FC<LayerCopyOptionsProps> = ({
  deps,
  options,
  changeOptions,
  isFiltered,
  filteredCount,
  tableInfo,
  limits,
}) => {
  const { t } = useTranslation("dialogs");

  const isTablesLimit: boolean = isInfinityLimit(limits.maxTablesCount)
    ? false
    : limits && limits.currentTableCount >= limits.maxTablesCount;

  if (!tableInfo.name) {
    return (
      <RestrictedWidthContainer>
        <LayerCopyFilteredOptionsContainer>
          <Notice warning>{t("copyResource.tableNotFoundMessage")}</Notice>
        </LayerCopyFilteredOptionsContainer>
      </RestrictedWidthContainer>
    );
  }

  const tablesReadableCount: number =
    deps && deps.tables
      ? deps.tables.filter(item => {
          return isReadable(item.permissions);
        }).length
      : 0;

  if (tablesReadableCount === 0) {
    return null;
  }

  return isFiltered && !isTablesLimit ? (
    <RestrictedWidthContainer>
      <LayerCopyFilteredOptionsContainer>
        <H3>{t("copyResource.copyingTable")}</H3>
        <OptionsRadioGroup onChange={e => changeOptions(e.target.value)} value={options}>
          <OptionsRadioContainer>
            <Radio value={FILTERED_OPTIONS.NO_TABLES} description={t("copyResource.sourceTableMessage")}>
              {t("copyResource.doNotCopy")}
            </Radio>
          </OptionsRadioContainer>
          <OptionsRadioContainer>
            <Radio value={FILTERED_OPTIONS.FILTERED_ONLY} description={t("copyResource.onlyFilteredFeaturesMessage")}>
              {`${t("copyResource.copyOnly")} ${formatNumber(filteredCount)}
                ${declension(
                  t("copyResource.feature1"),
                  t("copyResource.feature2"),
                  t("copyResource.feature3"),
                  filteredCount,
                )} ${t("copyResource.of")}
                ${formatNumber(tableInfo ? tableInfo.rowCount : 0)} ${t("copyResource.toNewTable")}`}
            </Radio>
          </OptionsRadioContainer>
          <OptionsRadioContainer>
            <Radio value={FILTERED_OPTIONS.FULL_DATA} description={t("copyResource.allFeaturesMessage")}>
              {t("copyResource.copyAllFeatures")}
            </Radio>
          </OptionsRadioContainer>
        </OptionsRadioGroup>
      </LayerCopyFilteredOptionsContainer>
    </RestrictedWidthContainer>
  ) : (
    <RestrictedWidthContainer>
      <LayerCopyOptionsContainer
        success={options === FILTERED_OPTIONS.FULL_DATA && !isTablesLimit}
        disabled={isTablesLimit}
      >
        <Switch
          checked={options === FILTERED_OPTIONS.FULL_DATA && !isTablesLimit}
          onChange={e => {
            changeOptions(
              e.target.value === "on"
                ? options === FILTERED_OPTIONS.FULL_DATA
                  ? FILTERED_OPTIONS.NO_TABLES
                  : FILTERED_OPTIONS.FULL_DATA
                : options === FILTERED_OPTIONS.NO_TABLES
                ? FILTERED_OPTIONS.FULL_DATA
                : FILTERED_OPTIONS.NO_TABLES,
            );
          }}
          disabled={isTablesLimit}
        />
        <LabelContainer>
          {!isTablesLimit ? t("copyResource.copyTable") : t("copyResource.tableLimitExceeded")}
          <Description>
            {!isTablesLimit
              ? options === FILTERED_OPTIONS.FULL_DATA
                ? t("copyResource.featuresCopyMessage")
                : t("copyResource.originalFeaturesMessage")
              : `${t("copyResource.youAlreadyHavePart1")} ${limits.currentTableCount} ${t("copyResource.of")} ${
                  limits.maxTablesCount
                } ${t("copyResource.youAlreadyHavePart2")}`}
          </Description>
        </LabelContainer>
      </LayerCopyOptionsContainer>
    </RestrictedWidthContainer>
  );
};
